import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, Layers, Plus, Wand2, FlaskConical, Search as SearchIcon } from 'lucide-react';
import Badge from '../common/Badge';
import Button from '../common/Button';
import Modal from '../common/Modal';
import FormControl from '../common/FormControl';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import FileAnalyticsAPI from '../file_analytics/fileAnalyticsService';
import { DataSource, SourceRule } from '../file_analytics/types';
import { usePermission } from '../../contexts/PermissionContext';
import CmSourceRuleEditor, { CmSourceRuleEditorHandle } from './CmSourceRuleEditor';
import '../file_analytics/FileAnalytics.css';

type RuleKind = 'clean' | 'formula' | 'lookup';

/** The three kinds of rule, as a person would describe them. */
const KINDS: Array<{ kind: RuleKind; icon: React.ReactNode; label: string; desc: string }> = [
  {
    kind: 'clean',
    icon: <Wand2 size={20} />,
    label: 'Cleaning',
    desc: 'Remove repeats, drop rows that are missing something, or change a column’s type.',
  },
  {
    kind: 'formula',
    icon: <FlaskConical size={20} />,
    label: 'Formula',
    desc: 'Work out a new column from the ones already there.',
  },
  {
    kind: 'lookup',
    icon: <SearchIcon size={20} />,
    label: 'Lookup',
    desc: 'Borrow a column from another data set, matched on a key.',
  },
];

/**
 * Preprocessing — every rule that prepares data, as one ordered list.
 *
 * The rules are written against a data set, but they are not a list per data
 * set: they carry one sequence for the whole entity and the run follows that
 * sequence, whichever data set each rule belongs to. Reading them per data set
 * hides the only thing that decides what happens first, which is how a rule came
 * to read a column that had not been filled in yet.
 *
 * So the order is the list. The data set is a column on the rule and a way to
 * filter, not a container the rules live inside.
 *
 * Writing a rule still happens per data set, because a rule needs that data
 * set's columns to be written against. The data set is therefore chosen first,
 * and the existing editor then opens unchanged.
 */

const KIND_VARIANT: Record<string, 'info' | 'success' | 'warning' | 'neutral'> = {
  clean: 'neutral',
  formula: 'info',
  lookup: 'warning',
};

const CmPreprocessing: React.FC = () => {
  const [rules, setRules] = useState<SourceRule[]>([]);
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [sourceFilter, setSourceFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [search, setSearch] = useState('');
  const [groupBySource, setGroupBySource] = useState(false);

  /**
   * Adding a rule, in two steps.
   *
   * A rule is written against a particular data set's columns, and the form
   * differs by kind. So both are chosen first, and only then does the editor's
   * own form open — already on the right kind, for the right data set.
   *
   * The editor is mounted out of sight while this happens. It holds the whole
   * form for all three kinds, and is driven straight to it rather than being
   * shown as a list the reader would then have to search.
   */
  const [addOpen, setAddOpen] = useState(false);
  const [addSource, setAddSource] = useState<DataSource | null>(null);
  const [addKind, setAddKind] = useState<RuleKind | null>(null);
  /** Set when a row was clicked: that rule is opened rather than a blank form. */
  const [openRuleId, setOpenRuleId] = useState<number | null>(null);
  /** How many times opening that rule has been tried while the editor loads. */
  const [openTries, setOpenTries] = useState(0);

  /** The data set whose editor is mounted, and the kind to open it on. */
  const [editing, setEditing] = useState<DataSource | null>(null);
  const [openOnKind, setOpenOnKind] = useState<RuleKind | null>(null);
  const editorRef = useRef<CmSourceRuleEditorHandle>(null);

  /**
   * Counts how many times Add has been used. The editor closes its own form
   * without telling anyone, so after a cancel it would still be mounted holding
   * the abandoned draft. Keying it on this count mounts a fresh one for every
   * attempt, which is simpler than teaching the editor to report a close.
   */
  const [addAttempt, setAddAttempt] = useState(0);

  /**
   * Once the editor is mounted for the chosen data set, tell it to open its own
   * form on the chosen kind. It has to be mounted first, which is why this
   * waits for that rather than happening at the moment of choosing.
   */
  useEffect(() => {
    if (!editing || !editorRef.current) return;

    // A row was clicked: open that rule, filled in. The editor loads its own
    // rules when it mounts, so this may run before they have arrived — it is
    // retried briefly rather than silently doing nothing.
    if (openRuleId != null) {
      if (editorRef.current.openEditById(openRuleId)) {
        setOpenRuleId(null);
      } else {
        // Setting the same value again would not re-run this, so a counter is
        // stepped instead. Bounded, so a rule that never arrives gives up
        // rather than retrying for ever.
        if (openTries < 25) {
          const t = window.setTimeout(() => setOpenTries(n => n + 1), 120);
          return () => window.clearTimeout(t);
        }
        setOpenRuleId(null);
        setEditing(null);
        setToast({ type: 'error', message: 'That rule could not be opened. Please refresh and try again.' });
      }
      return;
    }

    if (openOnKind) {
      editorRef.current.openAdd(openOnKind);
      setOpenOnKind(null);
    }
  }, [editing, openOnKind, openRuleId, openTries]);

  const { allowed: canModify } = usePermission('CMG_RULES', 'view+modify');
  const { allowed: canView, loading: viewLoading } = usePermission('CMG_RULES', 'view');

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        FileAnalyticsAPI.listPreparationRules(),
        FileAnalyticsAPI.listSources(),
      ]);
      setRules(r);
      setSources(s);
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Could not load the rules' });
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { load(); }, [load]);

  /**
   * What a rule does, in a few words. The stored shape differs by kind, so this
   * reads the field that carries the meaning rather than showing raw columns.
   */
  const describe = (r: SourceRule): string => {
    if (r.rule_type === 'formula') return r.expression || '';
    if (r.rule_type === 'lookup') {
      const cols = (r.selected_columns || []).join(', ');
      return `takes ${cols || 'columns'} from ${r.ref_source_name || 'another data set'}`;
    }
    const op = (r.operation || '').replace(/_/g, ' ');
    const col = (r.params as any)?.column;
    return col ? `${op} — ${col}` : op;
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rules.filter(r => {
      if (sourceFilter && String(r.source_id) !== sourceFilter) return false;
      if (kindFilter && r.rule_type !== kindFilter) return false;
      if (!term) return true;
      return (
        (r.source_name || '').toLowerCase().includes(term) ||
        (r.output_column || '').toLowerCase().includes(term) ||
        describe(r).toLowerCase().includes(term)
      );
    });
  }, [rules, sourceFilter, kindFilter, search]);

  /**
   * Grouping is for reading only. The sequence number stays on every row, so a
   * grouped view never hides that the order runs across data sets.
   */
  const grouped = useMemo(() => {
    if (!groupBySource) return [{ name: '', rules: filtered }];
    const by = new Map<string, SourceRule[]>();
    for (const r of filtered) {
      const k = r.source_name || `Data set ${r.source_id}`;
      if (!by.has(k)) by.set(k, []);
      by.get(k)!.push(r);
    }
    return Array.from(by, ([name, rs]) => ({ name, rules: rs }));
  }, [filtered, groupBySource]);

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to these rules." />
      </div>
    );
  }

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="netbody-card flex flex-col h-full" data-testid="cm-preprocessing-root">

        <div className="netbody-header">
          <span className="inline-flex items-center gap-2 text-[13px]">
            <Layers size={14} />
            <strong>{filtered.length}</strong>
            <span className="text-[var(--text-muted)]">
              {filtered.length === 1 ? 'rule' : 'rules'}
              {filtered.length !== rules.length && ` of ${rules.length}`}
              , in the order they run
            </span>
          </span>

          <div className="flex items-center gap-2 ml-auto">
            <FormControl.Select
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              className="filter-status"
            >
              <option value="">All data sets</option>
              {sources.map(s => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </FormControl.Select>

            <FormControl.Select
              value={kindFilter}
              onChange={e => setKindFilter(e.target.value)}
              className="filter-status"
            >
              <option value="">All kinds</option>
              <option value="clean">Cleaning</option>
              <option value="formula">Formula</option>
              <option value="lookup">Lookup</option>
            </FormControl.Select>

            <button
              type="button"
              className={`btn ${groupBySource ? 'btn-fill' : 'btn-outline'} btn-sm`}
              onClick={() => setGroupBySource(g => !g)}
              title="Group the list by data set. The order is unchanged — the step number stays on every row."
              data-testid="BTN_cm_prep_group"
            >
              Group by data set
            </button>

            <FormControl.Input
              type="text"
              placeholder="Search rules…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
            />

            {/* Adding asks two things first — which data set, and which kind —
                because the form differs by both. Only then does the form open. */}
            <Button
              variant="fill"
              icon={Plus}
              onClick={() => { setAddSource(null); setAddKind(null); setAddOpen(true); }}
              disabled={!canModify || sources.length === 0}
              data-testid="BTN_cm_prep_add"
            >
              Add rule
            </Button>
          </div>
        </div>

        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden">
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">Step</th>
                      <th className="table-list-th">Data set</th>
                      <th className="table-list-th">Kind</th>
                      <th className="table-list-th">What it does</th>
                      <th className="table-list-th">Produces</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading} aria-live="polite">
                    {loading && (
                      <Skeleton.TableRows cols={['2xs', 'sm', 'pill', 'lg', 'sm']} />
                    )}

                    {!loading && filtered.length === 0 && (
                      <tr>
                        <td colSpan={5} className="table-list-empty-cell">
                          <NoDataFound
                            title={rules.length === 0 ? 'No preparation rules yet' : 'Nothing matches'}
                            message={rules.length === 0
                              ? 'Rules that clean the data or add a column are written here, and run in the order shown.'
                              : 'No rule matches the current filters.'}
                          />
                        </td>
                      </tr>
                    )}

                    {!loading && grouped.map(g => (
                      <React.Fragment key={g.name || 'all'}>
                        {g.name && (
                          <tr>
                            <td colSpan={5} style={{
                              padding: '10px 12px 6px', fontSize: 12, fontWeight: 600,
                              color: 'var(--text-muted)',
                            }}>
                              {g.name}
                              <span style={{ fontWeight: 400 }}>
                                {' '}· {g.rules.length} {g.rules.length === 1 ? 'rule' : 'rules'}
                              </span>
                            </td>
                          </tr>
                        )}
                        {g.rules.map(r => (
                          <tr
                            key={r.id}
                            className="table-list-row"
                            style={{ cursor: canModify ? 'pointer' : 'default' }}
                            // Opening an existing rule goes straight to its own
                            // form, on its own kind — the same route as adding,
                            // without the two questions already answered by the
                            // rule itself.
                            onClick={() => {
                              const s = sources.find(x => String(x.id) === String(r.source_id));
                              if (!canModify || !s) return;
                              setAddAttempt(n => n + 1);
                              setOpenTries(0);
                              setOpenOnKind(null);
                              setOpenRuleId(Number(r.id));
                              setEditing(s);
                            }}
                            data-testid={`cm-prep-row-${r.id}`}
                          >
                            <td className="table-list-cell table-list-cell--sno"
                                style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {r.global_seq ?? '—'}
                            </td>
                            <td className="table-list-cell">
                              <span className="table-list-cell-title">
                                {r.source_name || `Data set ${r.source_id}`}
                              </span>
                            </td>
                            <td className="table-list-cell">
                              <Badge variant={KIND_VARIANT[r.rule_type] || 'neutral'} size="sm">
                                {r.rule_type === 'clean' ? 'Cleaning'
                                  : r.rule_type === 'formula' ? 'Formula' : 'Lookup'}
                              </Badge>
                            </td>
                            <td className="table-list-cell">
                              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                {describe(r)}
                              </span>
                            </td>
                            <td className="table-list-cell">
                              {r.output_column
                                ? <code style={{ fontSize: 12 }}>{r.output_column}</code>
                                : <span className="text-[var(--text-muted)]">—</span>}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step one: which data set, and which kind of rule. Both are needed
          before a form can be shown, because the form differs by both. */}
      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} size="md" closeOnOverlayClick closeOnEscape>
        <Modal.Header>Add a preparation rule</Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                1 · Which data set is this rule for?
              </div>
              <FormControl.Select
                value={addSource ? String(addSource.id) : ''}
                onChange={e => {
                  const s = sources.find(x => String(x.id) === e.target.value);
                  setAddSource(s ?? null);
                }}
                data-testid="SEL_cm_prep_add_source"
              >
                <option value="">Choose a data set…</option>
                {sources.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.name}</option>
                ))}
              </FormControl.Select>
            </div>

            <div style={{ opacity: addSource ? 1 : 0.45, pointerEvents: addSource ? 'auto' : 'none' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                2 · What kind of rule?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {KINDS.map(k => {
                  const chosen = addKind === k.kind;
                  return (
                    <button
                      key={k.kind}
                      type="button"
                      // Choosing the kind is the last thing needed, so it opens
                      // the form rather than waiting for a second confirmation
                      // that could only say the same thing.
                      onClick={() => {
                        setAddKind(k.kind);
                        if (!addSource) return;
                        setAddOpen(false);
                        setAddAttempt(n => n + 1);
                        setOpenRuleId(null);
                        setEditing(addSource);
                        setOpenOnKind(k.kind);
                      }}
                      data-testid={`BTN_cm_prep_kind_${k.kind}`}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 14,
                        padding: '12px 14px', borderRadius: 10, textAlign: 'left', width: '100%',
                        border: `1px solid ${chosen ? 'var(--primary,#4f46e5)' : 'var(--border-color,#e5e7eb)'}`,
                        background: chosen ? 'var(--brand-subtle,rgba(79,70,229,0.06))' : 'var(--bg-card,#fff)',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ color: 'var(--primary,#4f46e5)', marginTop: 1, flexShrink: 0 }}>
                        {k.icon}
                      </span>
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-heading)' }}>
                          {k.label}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                          {k.desc}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {addSource && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                The rule will run after every rule already written, and can be
                moved by changing its step afterwards.
              </div>
            )}
          </div>
        </Modal.Body>
        {/* No Continue: choosing the kind is itself the last step, and a button
            that only repeats the choice just made is one press too many. */}
        <Modal.Footer>
          <Button variant="secondary-outline" onClick={() => setAddOpen(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Step two: the editor's own form, opened directly on the chosen kind.
          It is mounted out of sight — only its form is wanted here, not the list
          of rules it also renders, which the page already shows in full. */}
      {editing && (
        <div style={{ position: 'fixed', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
          <CmSourceRuleEditor
            ref={editorRef}
            key={`prep-${editing.id}-${addAttempt}`}
            source={editing}
            columns={editing.standard_schema ?? []}
            readOnly={!canModify}
            onChanged={() => { load(); setEditing(null); }}
          />
        </div>
      )}
    </>
  );
};

export default CmPreprocessing;
